import { motion } from 'motion/react';
import { useState } from 'react';
import { PROPS_CATALOG, type CharacterProp } from '@/types/props';
import { useShop } from '@/contexts/ShopContext';
import { PixelIcon } from '@/components/ui';

interface PropShopSectionProps {
  filter?: string;
}

const PropShopSection: React.FC<PropShopSectionProps> = ({ filter = 'all' }) => {
  const { shopState, purchaseProp, equipProp, unequipProp } = useShop();
  const [selectedProp, setSelectedProp] = useState<CharacterProp | null>(null);

  // Filter props based on category
  const filteredProps = PROPS_CATALOG.filter(prop => {
    if (filter === 'all') return true;
    if (filter === 'owned') return shopState.ownedProps.includes(prop.id);
    if (filter === 'equipped') return shopState.equippedProps.includes(prop.id);
    return prop.rarity === filter;
  });

  const handlePurchase = (prop: CharacterProp) => {
    const canAffordCoins = !prop.price.coins || shopState.coins >= prop.price.coins;
    const canAffordGems = !prop.price.gems || shopState.gems >= prop.price.gems;
    
    if (canAffordCoins && canAffordGems) {
      const success = purchaseProp(prop.id);
      if (success) {
        // Show success animation
        console.log(`Purchased ${prop.name}!`);
      }
    }
  };

  const handleEquip = (prop: CharacterProp) => {
    if (shopState.equippedProps.includes(prop.id)) {
      unequipProp(prop.id);
    } else {
      equipProp(prop.id);
    }
  };

  const getRarityColor = (rarity: CharacterProp['rarity']) => {
    switch (rarity) {
      case 'common': return '#B0BEC5';
      case 'rare': return '#42A5F5';
      case 'epic': return '#AB47BC';
      case 'legendary': return '#FFD600';
      default: return '#B0BEC5';
    }
  };

  return (
    <div className="prop-shop-section">
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
        gap: '20px',
        padding: '10px 0'
      }}>
        {filteredProps.map(prop => {
          const isOwned = shopState.ownedProps.includes(prop.id);
          const isEquipped = shopState.equippedProps.includes(prop.id);
          const canAffordCoins = !prop.price.coins || shopState.coins >= prop.price.coins;
          const canAffordGems = !prop.price.gems || shopState.gems >= prop.price.gems;

          return (
            <motion.div
              key={prop.id}
              className="prop-card"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedProp(prop)}
              style={{
                border: `2px solid ${getRarityColor(prop.rarity)}`,
                borderRadius: '15px',
                padding: '15px',
                background: isOwned ? 'var(--pixel-bg-secondary)' : 'var(--pixel-bg-secondary)',
                cursor: 'pointer',
                position: 'relative',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div>
                {/* Prop Icon/Visual */}
                <div style={{
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '10px 0',
                  background: 'var(--pixel-bg)',
                  borderRadius: '10px',
                  fontSize: '32px',
                  color: prop.model.color
                }}>
                  {prop.model.icon || <PixelIcon name="settings" size={32} />}
                </div>

                {/* Prop Name */}
                <h3 style={{ 
                  margin: '0 0 5px 0', 
                  fontSize: '16px',
                  color: getRarityColor(prop.rarity),
                  textAlign: 'center'
                }}>
                  {prop.name}
                </h3>

                {/* Prop Description */}
                <p style={{ 
                  margin: '0 0 15px 0', 
                  fontSize: '12px', 
                  color: 'var(--pixel-text-secondary)',
                  lineHeight: '1.3',
                  textAlign: 'center'
                }}>
                  {prop.description}
                </p>
              </div>

              <div>
                {/* Effects */}
                <div style={{ marginBottom: '15px' }}>
                  {prop.effects.map((effect, i) => (
                    <div key={i} style={{
                      background: 'rgba(255,255,255,0.1)',
                      padding: '4px 8px',
                      borderRadius: '8px',
                      fontSize: '10px',
                      marginBottom: '4px',
                      border: `1px solid ${getRarityColor(prop.rarity)}`
                    }}>
                      {effect.description}
                    </div>
                  ))}
                </div>

                {/* Price or Status */}
                {!isOwned ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePurchase(prop);
                    }}
                    disabled={!canAffordCoins || !canAffordGems}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '10px',
                      background: (canAffordCoins && canAffordGems) ? 'var(--pixel-primary)' : 'var(--pixel-disabled)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: (canAffordCoins && canAffordGems) ? 'pointer' : 'not-allowed',
                      gap: '5px'
                    }}
                  >
                    {prop.price.coins && (
                      <>
                        <PixelIcon name="coin" size={16} color="#f39c12" />
                        {prop.price.coins}
                      </>
                    )}
                    {prop.price.coins && prop.price.gems && <span>+</span>}
                    {prop.price.gems && (
                      <>
                        <PixelIcon name="gem" size={16} color="#9b59b6" />
                        {prop.price.gems}
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEquip(prop);
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '10px',
                      background: isEquipped ? 'var(--pixel-success)' : 'var(--pixel-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      gap: '5px'
                    }}
                  >
                    <PixelIcon name={isEquipped ? "check" : "settings"} size={16} />
                    {isEquipped ? 'Equipped' : 'Equip'}
                  </button>
                )}
              </div>

              {/* Equipped Badge */}
              {isEquipped && (
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'var(--pixel-success)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <PixelIcon name="check" size={12} /> Equipped
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Prop Detail Modal */}
      {selectedProp && (
        <motion.div
          className="prop-detail-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSelectedProp(null)}
        >
          <motion.div
            className="prop-detail-content"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ color: getRarityColor(selectedProp.rarity) }}>
              {selectedProp.name}
            </h2>
            <div className="prop-detail-visual" style={{ color: selectedProp.model.color }}>
              {selectedProp.model.icon || ''}
            </div>
            <p className="prop-detail-description">{selectedProp.description}</p>
            
            <div className="prop-detail-effects">
              <h3>Effects:</h3>
              {selectedProp.effects.map((effect, i) => (
                <div key={i} className="effect-detail">
                  <strong>{effect.type}:</strong> {effect.description}
                </div>
              ))}
            </div>

            <div className="prop-detail-actions">
              {!shopState.ownedProps.includes(selectedProp.id) ? (
                <button
                  className="purchase-button"
                  onClick={() => handlePurchase(selectedProp)}
                  disabled={
                    !!(selectedProp.price.coins && shopState.coins < selectedProp.price.coins) ||
                    !!(selectedProp.price.gems && shopState.gems < selectedProp.price.gems)
                  }
                >
                  Purchase
                </button>
              ) : (
                <button
                  className="equip-button"
                  onClick={() => handleEquip(selectedProp)}
                >
                  {shopState.equippedProps.includes(selectedProp.id) ? 'Unequip' : 'Equip'}
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default PropShopSection;